import React, { useState, useEffect } from 'react';
import { firestore } from '../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { useUser } from '../../hooks/UserContext';
import { logAdminOperation } from '../../lib/adminActivityLogger';
import ReleaseNoteList from '../../components/admin/releasenotes/ReleaseNoteList';
import ReleaseNoteForm from '../../components/admin/releasenotes/ReleaseNoteForm';

function ReleaseNoteManagement() {
  const { user } = useUser();
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [loading, setLoading] = useState(true);

  const releaseNotesCollection = collection(firestore, 'releaseNotes');

  const fetchData = async () => {
    setLoading(true);
    try {
      const q = query(releaseNotesCollection, orderBy('publishedAt', 'desc'));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setReleaseNotes(list);
    } catch (error) {
      console.error("Error fetching release notes: ", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (note) => {
    setSelectedNote(note);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleCancel = () => setSelectedNote(null);

  const handleSave = async (noteData) => {
    const { id, ...dataToSave } = noteData;
    try {
      if (id) {
        const noteRef = doc(firestore, 'releaseNotes', id);
        await updateDoc(noteRef, { 
          ...dataToSave, 
          updatedAt: serverTimestamp(),
          updatedBy: user?.uid || 'unknown'
        });

        await logAdminOperation({
          operation_type: 'release_note_update',
          target_type: 'release_note',
          target_id: id,
          target_name: dataToSave.title || '',
          reason: 'リリースノート更新'
        });

        alert("リリースノートが更新されました。");
      } else {
        const newDoc = await addDoc(releaseNotesCollection, { 
          ...dataToSave, 
          createdAt: serverTimestamp(), 
          updatedAt: serverTimestamp(),
          createdBy: user?.uid || 'unknown',
          updatedBy: user?.uid || 'unknown'
        });

        await logAdminOperation({
          operation_type: 'release_note_create',
          target_type: 'release_note',
          target_id: newDoc.id,
          target_name: dataToSave.title || '',
          reason: 'リリースノート作成'
        });

        alert("リリースノートが作成されました。");
      }
      setSelectedNote(null);
      fetchData();
    } catch (error) {
      console.error("Error saving release note: ", error);
      alert("保存に失敗しました。");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('このリリースノートを削除してもよろしいですか？')) {
      try {
        const targetNote = releaseNotes.find(r => r.id === id);
        await deleteDoc(doc(firestore, 'releaseNotes', id));

        await logAdminOperation({
          operation_type: 'release_note_delete',
          target_type: 'release_note',
          target_id: id,
          target_name: targetNote ? targetNote.title : '',
          reason: 'リリースノート削除'
        });

        alert("削除されました。");
        if (selectedNote && selectedNote.id === id) setSelectedNote(null);
        fetchData();
      } catch (error) {
        console.error("Error deleting release note: ", error);
        alert("削除に失敗しました。");
      }
    }
  };

  if (loading && releaseNotes.length === 0) return <div className="p-8 text-center">読み込み中...</div>;

  return (
    <div className="container mx-auto p-8">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">リリースノート管理</h1>
      
      <div className="mb-12">
        <h2 className="text-2xl font-semibold mb-4">
          {selectedNote ? 'リリースノートを編集' : '新規リリースノート作成'}
        </h2>
        <ReleaseNoteForm 
          releaseNote={selectedNote} 
          onSave={handleSave} 
          onCancel={handleCancel}
        />
      </div>

      <hr className="my-12" />

      <div>
        <h2 className="text-2xl font-semibold mb-4">リリースノート一覧</h2>
        <ReleaseNoteList 
          releaseNotes={releaseNotes} 
          onEdit={handleEdit} 
          onDelete={handleDelete} 
        />
      </div>
    </div>
  );
}

export default ReleaseNoteManagement;
